import React from 'react';
import PropTypes from 'prop-types';

import {Box, Chip, Divider, Stack, Typography, screenSizeBigger, StickyTooltip} from 'views/components';
import {Check} from 'views/icons';
import {ConnectionBadge, HoverToolBox, MarkdownViewer, Translate, translate} from 'views/module';

import moment from 'moment';
import {TASK_TYPE_CONFIG} from '../../utils/taskTypeConfig';

const TaskItem = (props) => {
  const {
    task,
    handleSetStatusToComplete,
    onClickEditTask,
    removeTask,
  } = props;
  const isStatusComplete = task.status === 'COMPLETE';
  const largerScreen = screenSizeBigger('sm');

  const isDueDateADate = moment(task.dueDate, moment.ISO_8601, true).isValid();
  const dueDateMoment = isDueDateADate ? moment(task.dueDate) : null;
  const isOverdue = isDueDateADate ?
    dueDateMoment.isBefore(moment()) :
    false;
  const overdueDayCount = isDueDateADate ?
    moment().startOf('day').diff(dueDateMoment.clone().startOf('day'), 'days') :
    0;
  const maxDisplayedAvatarIcons = 5;
  const connections = task.connectionInfo || [];
  const visibleConnections = connections.slice(0, maxDisplayedAvatarIcons);
  const hiddenConnections = connections.slice(maxDisplayedAvatarIcons);
  const hasDueDateDisplay = isDueDateADate || Boolean(task.dueDate && !isDueDateADate);

  const [toolbarVisible, setToolbarVisible] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const rightEdgeAnchorRef = React.useRef(null);

  const handleMouseEnter = () => {
    if (largerScreen) {
      setToolbarVisible(true);
      setAnchorEl(rightEdgeAnchorRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (largerScreen) {
      setToolbarVisible(false);
      setAnchorEl(null);
    }
  };

  React.useEffect(() => {
    setToolbarVisible(false);
    setAnchorEl(null);
  }, [largerScreen]);

  const getConnectionDisplayName = (connection) => [connection.firstName, connection.lastName].filter(Boolean).join(' ');

  return (
    <Stack
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        opacity: isStatusComplete ? 0.45 : 1,
      }}
    >
      <Stack
        spacing={1.25}
        sx={{
          py: 2,
          pr: 3,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
        >
          <Box
            onClick={() => handleSetStatusToComplete(task.taskRn, isStatusComplete)}
            sx={{
              'width': 24,
              'height': 24,
              'borderRadius': '50%',
              'border': '1.5px solid',
              'borderColor': isStatusComplete ? 'success.main' : 'divider',
              'backgroundColor': isStatusComplete ? 'success.main' : 'transparent',
              'display': 'flex',
              'alignItems': 'center',
              'justifyContent': 'center',
              'cursor': 'pointer',
              '& .todo-check': {
                transition: 'color 120ms ease',
                color: isStatusComplete ? 'common.white' : 'transparent',
                fontSize: 10,
              },
              '&:hover': {
                borderColor: isStatusComplete ? 'success.main' : 'success.main',
              },
              '&:hover .todo-check': {
                color: isStatusComplete ? 'common.white' : 'success.main',
              },
            }}
          >
            <Check className="todo-check" />
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              flexWrap: 'wrap',
              flex: 1,
            }}
          >
            {
              task?.type && (() => {
                const type = task.type;
                const typeConfig = TASK_TYPE_CONFIG[type] ?? TASK_TYPE_CONFIG.UNKNOWN;

                return (
                  <Chip
                    key={type}
                    variant="outlined"
                    label={typeConfig.labelKey ? translate(typeConfig.labelKey) : type}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      ...typeConfig.chipSx,
                      borderWidth: 1,
                    }}
                  />
                );
              })()
            }
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                textDecoration: isStatusComplete ? 'line-through' : 'none',
              }}
            >
              {task.title}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          spacing={1}
          sx={{
            pl: '40px',
          }}
        >
          <MarkdownViewer
            value={task.description?.blob || translate('tasks.MyDay.TaskItem.Description.Empty')}
            truncate
          />
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              flexWrap: 'wrap',
            }}
          >
            {
              isDueDateADate &&
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: isOverdue && !isStatusComplete ? 'error.main' : 'text.secondary',
                }}
              >
                {
                  overdueDayCount === 1 && (
                    <Translate
                      expression="tasks.MyDay.TaskItem.Due.YesterdayLabel"
                      substitute={{time: dueDateMoment.format('h:mmA')}}
                    />
                  )
                }

                {
                  overdueDayCount > 1 &&
                    <Translate
                      expression="tasks.MyDay.TaskItem.Due.DaysAgoLabel"
                      substitute={{
                        days: overdueDayCount,
                        time: dueDateMoment.format('h:mmA'),
                      }}
                    />
                }
                {
                  overdueDayCount < 1 &&
                    <Translate
                      expression="tasks.MyDay.TaskItem.Due.Label"
                      substitute={{time: dueDateMoment.format('h:mmA')}}
                    />
                }
              </Typography>
            }
            {
              (task.dueDate && !isDueDateADate) &&
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                }}
              >
                {task.dueDate}
              </Typography>
            }
            <Box
              sx={{
                display: hasDueDateDisplay && connections.length > 0 ? 'block' : 'none',
                height: 4,
                width: 4,
                borderRadius: '50%',
                backgroundColor: 'text.secondary',
              }}
            />
            {
              connections.length > 0 &&
                <Stack
                  direction="row"
                  spacing={-.5}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  {
                    visibleConnections.map((connection, index) => (
                      <ConnectionBadge
                        key={connection.connectionRn}
                        connection={connection}
                        sx={{
                          width: '28px',
                          height: '28px',
                          position: 'relative',
                          zIndex: index + 1,
                          fontSize: '13px',
                          outline: '2px solid',
                          outlineColor: 'background.paper',
                        }}
                      />
                    ))
                  }
                  {
                    hiddenConnections.length > 0 &&
                      <StickyTooltip
                        title={hiddenConnections.map((connection) => getConnectionDisplayName(connection)).join(', ')}
                      >
                        <Box
                          sx={{
                            width: '28px',
                            height: '28px',
                            position: 'relative',
                            zIndex: visibleConnections.length + 1,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#E2DEDA',
                            color: 'text.secondary',
                            fontSize: '11px',
                            fontWeight: 600,
                            outline: '2px solid',
                            outlineColor: 'background.paper',
                          }}
                        >
                          +{hiddenConnections.length}
                        </Box>
                      </StickyTooltip>
                  }
                </Stack>
            }
          </Stack>
        </Stack>
      </Stack>
      <Box
        ref={rightEdgeAnchorRef}
        sx={{
          position: 'absolute',
          right: -100,
          top: 20,
          width: 0,
          height: 0,
        }}
      />
      <HoverToolBox
        toolBar={toolbarVisible}
        anchorEl={anchorEl}
        onClickEdit={() => onClickEditTask(task)}
        onClickDelete={() => removeTask(task)}
        hideTool={{
          emoji: true,
          copy: true,
          edit: false,
          delete: false,
        }}
      />
      <Divider />
    </Stack>
  );
};

TaskItem.propTypes = {
  task: PropTypes.shape({
    taskRn: PropTypes.string.isRequired,
    type: PropTypes.string,
    title: PropTypes.string.isRequired,
    clientRn: PropTypes.string,
    clientName: PropTypes.string,
    description: PropTypes.shape({
      blob: PropTypes.string,
      editorType: PropTypes.string,
    }),
    ownerRn: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    connections: PropTypes.arrayOf(PropTypes.string),
    connectionInfo: PropTypes.arrayOf(PropTypes.shape({
      connectionRn: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    })),
    updatedAt: PropTypes.string,
    createdAt: PropTypes.string,
    dueDate: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  handleSetStatusToComplete: PropTypes.func.isRequired,
  onClickEditTask: PropTypes.func,
  removeTask: PropTypes.func,
};

export default (React.memo(TaskItem));

